import {
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react'
import { appendSinIvaQueryParam } from '../../utils/printDocumentIva'
import { DIGITAL_APP_BASE } from '../../constants/apiBase'

// const baseUrl = 'http://localhost:3001'
const baseUrl = DIGITAL_APP_BASE

const pdfBlobResponseHandler = async (response) => {
  if (!response.ok) {
    throw new Error(
      (await response.text()) ||
        `HTTP error! Status: ${response.status}`,
    )
  }
  return response.blob()
}

export const digitalAppApi = createApi({
  reducerPath: 'digitalAppApi',
  baseQuery: fetchBaseQuery({ baseUrl }),
  tagTypes: [
    'Contacts',
    'FacturaHistory',
    'Orders',
    'Products',
    'Sales',
    'Statuses',
  ],
  endpoints: (builder) => ({
    //SUppliers credentials
    createOrderTPO: builder.mutation({
      query: (body) =>  ({
        url: '/api/createOrderAdminBrowser',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'PUT',
        body
      })
    }),

    // --- Shop catalog / landing (droplet parity via /api/shop) ---
    findAllWords: builder.query({
      query: () => ({
        url: `/api/shop/products-enabled`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: [{ type: 'Products', id: 'LIST' }],
      keepUnusedDataFor: 36,
    }),
    findAllWordsByTradeName: builder.query({
      query: (tradeName) => ({
        url: `/api/shop/products-by-trade-name`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { tradeName },
      }),
      providesTags: [{ type: 'Products', id: 'LIST' }],
      keepUnusedDataFor: 36,
    }),
    getProductById: builder.query({
      query: (id) => ({
        url: `/api/shop/products/${id}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    getProductsAdminSearchInput: builder.query({
      query: (name) => ({
        url: `/api/shop/products/search-by-name`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { name },
      }),
    }),
    getCategoryList: builder.query({
      query: () => ({
        url: `/api/shop/landing/categories`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    getBrandsList: builder.query({
      query: () => ({
        url: `/api/shop/landing/brands`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    getBusinessTypeList: builder.query({
      query: () => ({
        url: `/api/shop/landing/business-types`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
    }),
    getClientPersonalSales: builder.query({
      query: (tradeName) => ({
        url: `/api/shop/personal-sales`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { tradeName },
      }),
    }),
    getContactTradeNameByPhone: builder.query({
      query: (search) => ({
        url: `/api/shop/contact-by-phone-search`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { search },
      }),
    }),
    getContactAmbassadorByTradeNameAdmin: builder.query({
      query: (tradeName) => ({
        url: `/api/shop/ambassador`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { tradeName },
      }),
    }),
    getStatsByTradeNames: builder.query({
      query: (tradeNames) => ({
        url: `/api/shop/stats-by-trade-names`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { tradeNames },
      }),
    }),
    getOrdersByWeekAdmin: builder.query({
      query: (tradeNames) => ({
        url: `/api/shop/orders-by-week`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { tradeNames },
      }),
    }),
    updateCurrentCartContact: builder.mutation({
      query: (body) => ({
        url: `/api/shop/current-cart`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
    }),
    addClientPersonalPhones: builder.mutation({
      query: (body) => ({
        url: `/api/shop/add-personal-phone`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
    }),
    acceptLoginAcc: builder.mutation({
      query: (body) => ({
        url: `/api/shop/accept-login`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
    }),
    createContactAdmin: builder.mutation({
      query: (body) => ({
        url: `/api/shop/create-contact`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
    }),
    updateContactAdmin: builder.mutation({
      query: (body) => ({
        url: `/api/shop/update-contact`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
      invalidatesTags: [{ type: 'Contacts', id: 'LIST' }],
    }),
    addLog: builder.mutation({
      query: (body) => ({
        url: `/api/logs`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
    }),

    // --- Droplet-parity: contacts ---
    getBusinessContactByTradeName: builder.query({
      query: (tradeName) => ({
        url: `/api/contacts/client-by-trade-name`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { tradeName },
      }),
      providesTags: (result) =>
        result?._id
          ? [{ type: 'Contacts', id: result._id }]
          : [{ type: 'Contacts', id: 'CLIENT' }],
    }),
    getContactsLegacyList: builder.query({
      query: () => ({
        url: `/api/contacts/legacy-list`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: [{ type: 'Contacts', id: 'LIST' }],
    }),
    updatePaidByTradeName: builder.mutation({
      query: (body) => ({
        url: `/api/contacts/update-paid-by-trade-name`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
      invalidatesTags: [{ type: 'Contacts', id: 'LIST' }],
    }),

    // --- Droplet-parity: facturas ---
    getFacturasByClient: builder.query({
      query: (query) => ({
        url: `/api/factura-history/by-client`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { tradeName: query?.tradeName || query || '' },
      }),
      transformResponse: (response) =>
        Array.isArray(response) ? response.slice().reverse() : response,
      providesTags: ['FacturaHistory'],
    }),
    updateFacturaUpdateField: builder.mutation({
      query: (body) => ({
        url: `/api/factura-history/update-field`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
      invalidatesTags: ['FacturaHistory'],
    }),

    // --- Droplet-parity: orders ---
    getOrdersByTradeName: builder.query({
      query: (tradeName) => ({
        url: `/api/orders/legacy-by-trade-name`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { tradeName },
      }),
      transformResponse: (response) =>
        Array.isArray(response) ? response.slice().reverse() : response,
      providesTags: ['Orders'],
    }),
    getOrderItemsByTradeName: builder.query({
      query: (tradeName) => ({
        url: `/api/orders/fast-order-items`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { tradeName },
      }),
      providesTags: ['Products'],
    }),
    getOrdersByIds: builder.query({
      query: (ids) => ({
        url: `/api/orders/by-ids`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: { ids },
      }),
      providesTags: ['Orders'],
    }),
    getOrderById: builder.query({
      query: (id) => ({
        url: `/api/orders/legacy/${id}`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: (result, error, id) => [{ type: 'Orders', id }],
    }),
    updateOrderItemsAdmin: builder.mutation({
      query: (body) => ({
        url: `/api/updateOrderItemsAdmin`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
      invalidatesTags: ['Orders'],
    }),
    updateOrderDescription: builder.mutation({
      query: (body) => ({
        url: `/api/orders/change-description`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
      invalidatesTags: ['Orders'],
    }),
    updateOrderStatus: builder.mutation({
      query: (body) => ({
        url: `/api/updateStatusOrderAdmin`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
      invalidatesTags: ['Orders'],
    }),

    // --- Droplet-parity: catalog ---
    getProductsBySkus: builder.query({
      query: (query) => ({
        url: `/api/catalog/products-by-skus`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: {
          skus: typeof query === 'string' ? query : query?.skus || '',
        },
      }),
      providesTags: ['Products'],
    }),

    // --- Droplet-parity: statuses ---
    getAllStatuses: builder.query({
      query: (siteTradename) => ({
        url: `/api/statuses`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: siteTradename ? { siteTradename } : undefined,
      }),
      providesTags: ['Statuses'],
    }),

    // --- Droplet-parity: sales / alerts ---
    getSales: builder.query({
      query: () => ({
        url: `/api/sales`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }),
      providesTags: ['Sales'],
    }),
    createSale: builder.mutation({
      query: (body) => ({
        url: `/api/sales`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
      invalidatesTags: ['Sales'],
    }),

    getAlbaranById: builder.query({
      query: (params) => {
        const { id, tradeName, sinIva } = params || {}
        const queryParams = new URLSearchParams()

        if (tradeName)
          queryParams.append('tradeName', tradeName)
        appendSinIvaQueryParam(queryParams, sinIva)

        const queryString = queryParams.toString()
        const url = `/api/print-document/albaran/${id}${
          queryString ? `?${queryString}` : ''
        }`

        return {
          url,
          headers: {
            'Content-Type': 'application/pdf',
          },
          method: 'GET',
          responseHandler: pdfBlobResponseHandler,
        }
      },
    }),
    getCheckListById: builder.query({
      query: (params) => {
        const { id, tradeName, sinIva } = params || {}
        const queryParams = new URLSearchParams()

        if (tradeName)
          queryParams.append('tradeName', tradeName)
        appendSinIvaQueryParam(queryParams, sinIva)

        const queryString = queryParams.toString()
        const url = `/api/print-document/checklist/${id}${
          queryString ? `?${queryString}` : ''
        }`

        return {
          url,
          headers: {
            'Content-Type': 'application/pdf',
          },
          method: 'GET',
          responseHandler: pdfBlobResponseHandler,
        }
      },
    }),
    getInvoiceById: builder.query({
      query: (params) => {
        const {
          orderId,
          accountKey,
          number,
          numberInYear,
          orderIds,
          date,
          tradeName,
          sinIva,
        } = params || {}
        const queryParams = new URLSearchParams()

        if (accountKey)
          queryParams.append('accountKey', accountKey)
        if (number) queryParams.append('number', number)
        if (numberInYear)
          queryParams.append('numberInYear', numberInYear)
        if (orderIds)
          queryParams.append('orderIds', orderIds)
        if (date) queryParams.append('date', date)
        if (tradeName)
          queryParams.append('tradeName', tradeName)
        appendSinIvaQueryParam(queryParams, sinIva)

        const queryString = queryParams.toString()
        const url = orderId
          ? `/api/print-document/invoice/${orderId}${queryString ? `?${queryString}` : ''}`
          : `/api/print-document/invoice/any${queryString ? `?${queryString}` : ''}`

        return {
          url,
          headers: {
            'Content-Type': 'application/pdf',
          },
          method: 'GET',
          responseHandler: pdfBlobResponseHandler,
        }
      },
    }),
    getInvoiceDetailed: builder.query({
      query: (params) => {
        const {
          accountKey,
          orderIds,
          number,
          numberInYear,
          date,
          tradeName,
          sinIva,
        } = params || {}
        const queryParams = new URLSearchParams()

        if (accountKey)
          queryParams.append('accountKey', accountKey)
        if (orderIds)
          queryParams.append('orderIds', orderIds)
        if (number) queryParams.append('number', number)
        if (numberInYear)
          queryParams.append('numberInYear', numberInYear)
        if (date) queryParams.append('date', date)
        if (tradeName)
          queryParams.append('tradeName', tradeName)
        appendSinIvaQueryParam(queryParams, sinIva)

        const queryString = queryParams.toString()

        return {
          url: `/api/print-document/invoice-detailed${queryString ? `?${queryString}` : ''}`,
          headers: {
            'Content-Type': 'application/pdf',
          },
          method: 'GET',
          responseHandler: pdfBlobResponseHandler,
        }
      },
    }),

    getContacts: builder.query({
      query: (params) => ({
        url: `/api/contacts`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((c) => ({
                type: 'Contacts',
                id: c._id,
              })),
              { type: 'Contacts', id: 'LIST' },
            ]
          : [{ type: 'Contacts', id: 'LIST' }],
    }),

    // Получить контакт по id
    getContactById: builder.query({
      query: (id) => ({
        url: `/api/contacts/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: (result, error, id) => [
        { type: 'Contacts', id },
      ],
    }),

    getMakroPersonal: builder.query({
      query: (tradeName) => ({
        url: `/api/contacts/marco-personal?tradeName=${tradeName}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      providesTags: (result, error, id) => [
        { type: 'Contacts', id },
      ],
    }),


    // Создать контакт
    createContact: builder.mutation({
      query: (body) => ({
        url: `/api/contacts`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
      invalidatesTags: [{ type: 'Contacts', id: 'LIST' }],
    }),

    // Получить контакт по tradeName
    getContactByTradeName: builder.query({
      query: (tradeName) => ({
        url: `/api/contacts/trade-name`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { tradeName },
      }),
      providesTags: (result) =>
        result?.data?._id
          ? [{ type: 'Contacts', id: result.data._id }]
          : [],
    }),

    // Получить контакт по email
    getContactByEmail: builder.query({
      query: (email) => ({
        url: `/api/contacts/email`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { email },
      }),
      providesTags: (result) =>
        result?.data?._id
          ? [{ type: 'Contacts', id: result.data._id }]
          : [],
    }),

    // Получить контакт по phone
    getContactByPhone: builder.query({
      query: (phone) => ({
        url: `/api/contacts/phone`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        params: { phone },
      }),
      providesTags: (result) =>
        result?.data?._id
          ? [{ type: 'Contacts', id: result.data._id }]
          : [],
    }),

    // Получить список типов контактов
    getContactTypes: builder.query({
      query: () => ({
        url: `/api/contacts/types`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    trackCategoryVisit: builder.mutation({
      query: (body) => ({
        url: `/api/category-visits/track`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
    }),
  }),
})
